<template>
  <div class="consult-detail">
    <div class="card">
      <div class="card-header">
        <el-breadcrumb>
          <el-breadcrumb-item>咨询运营</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/consult' }">咨询线索</el-breadcrumb-item>
          <el-breadcrumb-item>线索详情</el-breadcrumb-item>
        </el-breadcrumb>
        <el-button plain @click="$router.push('/consult')">返回列表</el-button>
      </div>

      <div v-if="lead" class="consult-detail__profile">
        <div><span>孩子</span>{{ lead.child_name || '-' }}</div>
        <div><span>性别</span>{{ sexText(lead.child_sex) }}</div>
        <div><span>电话</span>{{ lead.phone || '-' }}</div>
        <div><span>来源</span>{{ lead.source || '-' }}</div>
        <div><span>状态</span>{{ statusText(lead.status) }}</div>
        <div><span>最近咨询</span>{{ lead.last_contact_time || '-' }}</div>
      </div>

      <div class="consult-detail__section">
        <div class="consult-detail__heading">客服流程</div>
        <div v-if="workflowSummary" class="consult-workflow">
          <div class="consult-workflow__overview">
            <div><span>当前阶段</span>{{ workflowStateText(workflowSummary.state) }}</div>
            <div><span>最后话术</span>{{ workflowSummary.last_reply_key || '-' }}</div>
            <div><span>已收图片</span>{{ workflowSummary.image_count || 0 }} 张</div>
            <div><span>人工接管</span>{{ workflowSummary.handoff_reason || '否' }}</div>
          </div>
          <div class="consult-workflow__group">
            <div class="consult-workflow__label">已收资料</div>
            <div v-if="workflowFields.length" class="consult-workflow__fields">
              <div v-for="field in workflowFields" :key="field.key"><span>{{ field.label }}</span>{{ formatWorkflowValue(field.key, field.value) }}</div>
            </div>
            <div v-else class="consult-detail__empty">尚未收集到结构化资料</div>
          </div>
          <div class="consult-workflow__group">
            <div class="consult-workflow__label">待补资料</div>
            <div v-if="workflowSummary.missing_fields && workflowSummary.missing_fields.length" class="consult-workflow__missing">{{ workflowSummary.missing_fields.map(fieldLabel).join('、') }}</div>
            <div v-else class="consult-workflow__complete">基础资料已收集完毕，等待人工核对。</div>
          </div>
          <div v-if="workflowSummary.tags && workflowSummary.tags.length" class="consult-workflow__group">
            <div class="consult-workflow__label">运营标签</div>
            <el-tag v-for="tag in workflowSummary.tags" :key="tag" size="mini" class="consult-workflow__tag">{{ tag }}</el-tag>
          </div>
        </div>
        <div v-else class="consult-detail__empty">暂无客服流程记录</div>
      </div>

      <div class="consult-detail__section">
        <div class="consult-detail__heading">会话记录</div>
        <div v-if="!messages.length" class="consult-detail__empty">暂无会话记录</div>
        <div v-for="message in messages" :key="message.id" class="consult-message" :class="`consult-message--${message.direction}`">
          <div class="consult-message__meta">{{ message.direction === 'incoming' ? '用户' : '客服' }} · {{ message.create_time }}</div>
          <div class="consult-message__content">{{ message.content || message.msg_type }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'consult-detail',
  data() {
    return { lead: null, messages: [] }
  },
  computed: {
    workflowSummary() {
      if (!this.lead || !this.lead.extra || !this.lead.extra.customer_service) return null
      return this.lead.extra.customer_service
    },
    workflowFields() {
      if (!this.workflowSummary || !this.workflowSummary.collected_fields) return []
      return Object.keys(this.workflowSummary.collected_fields).map(key => ({
        key,
        label: this.fieldLabel(key),
        value: this.workflowSummary.collected_fields[key]
      }))
    }
  },
  created() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      const id = this.$route.query.id
      if (!id) {
        this.$router.push('/consult')
        return
      }
      const res = await this.$request(`consult-lead/${id}`)
      if (res && res.error_code === 10000) {
        this.lead = res.data.lead
        this.messages = res.data.messages || []
      }
    },
    sexText(sex) {
      return Number(sex) === 1 ? '男' : Number(sex) === 2 ? '女' : '-'
    },
    workflowStateText(state) {
      return {
        entry: '刚进入咨询',
        awaiting_confirmation: '等待确认咨询需求',
        collecting_profile: '收集基础资料',
        confirming_measurement: '核对测量信息',
        collecting_reason: '了解咨询原因',
        collecting_materials: '收集图片资料',
        awaiting_human_review: '等待人工核对'
      }[state] || state || '-'
    },
    fieldLabel(key) {
      return {
        child_name: '孩子姓名', child_sex: '性别', birthday: '出生日期', phone: '联系电话',
        height: '当前身高', weight: '体重', father_height: '父亲身高', mother_height: '母亲身高', hope_height: '期望身高',
        film_height: '拍片时身高', film_date: '拍片日期', measure_time: '测量时间', measurement_method: '测量方式',
        visit_type: '初诊/复诊', interventions: '既往干预', material_label: '图片资料类型'
      }[key] || key
    },
    formatWorkflowValue(key, value) {
      if (key === 'child_sex') return this.sexText(value)
      if (key === 'measurement_method') return value === 'electronic' ? '电子测量' : value === 'manual' ? '人工测量' : value
      if (key === 'visit_type') return value === 'initial' ? '初诊' : value === 'follow_up' ? '复诊' : value
      if (key === 'material_label') return { tongue_coating: '舌苔照片', left_wrist_xray: '左手腕 X 光片', other: '其他资料' }[value] || value
      return value
    },
    statusText(status) {
      return { new: '新线索', contacted: '已联系', qualified: '已合格', 'handed-off': '已转人工', closed: '已关闭' }[status] || status
    }
  }
}
</script>

<style scoped>
.consult-detail__profile { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; padding: 22px; border-radius: 6px; background: #f5faf8; }
.consult-detail__profile span { display: block; margin-bottom: 6px; color: #718077; font-size: 12px; }
.consult-detail__section { margin-top: 28px; }
.consult-detail__heading { margin-bottom: 12px; color: #27473d; font-weight: 600; }
.consult-detail__empty { color: #8a9991; }
.consult-workflow { padding: 18px; border-radius: 6px; background: #f7f9f8; }
.consult-workflow__overview, .consult-workflow__fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 20px; }
.consult-workflow__overview span, .consult-workflow__fields span { display: block; margin-bottom: 5px; color: #82918a; font-size: 12px; }
.consult-workflow__group { margin-top: 20px; }
.consult-workflow__label { margin-bottom: 9px; color: #466458; font-size: 13px; font-weight: 600; }
.consult-workflow__missing { color: #9b6d2e; line-height: 1.7; }
.consult-workflow__complete { color: #4c8069; }
.consult-workflow__tag { margin-right: 7px; }
.consult-message { max-width: 72%; margin: 12px 0; padding: 12px 14px; border-radius: 6px; background: #f2f5f4; }
.consult-message--outgoing { margin-left: auto; color: #173d32; background: #e2f3eb; }
.consult-message__meta { margin-bottom: 6px; color: #82918a; font-size: 12px; }
.consult-message__content { white-space: pre-wrap; line-height: 1.6; }
</style>
